import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BarChart3, ClipboardList, Eye, LayoutDashboard, UsersRound } from "lucide-react";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { usePerformanceLensAccess } from "../../lib/performance-lens/use-access.ts";
import { getGiveFeedback } from "../../lib/performance-lens/review-api.ts";
import type { GiveFeedbackData } from "../../lib/performance-lens/types.ts";
import GiveFeedbackPage from "./give-feedback-page.tsx";
import SelfCheckPage from "./self-check-page.tsx";
import MyFeedbackPage from "./my-feedback-page.tsx";
import PerformanceLensAdminPage from "./admin-dashboard-page.tsx";
import { PmDashboardPage, TeamSubmissionsPage } from "./pm-submissions-page.tsx";
import HodOverviewPage from "./hod-overview-page.tsx";
import TransparencyPage from "./transparency-page.tsx";
import ScreenSkeleton from "../../components/ui/screen-skeleton/screen-skeleton.tsx";
import styles from "./performance-workspace-page.module.css";
import UpcomingCycleBanner from "./upcoming-cycle-banner.tsx";

type PersonalTab = "feedback" | "self" | "results";
type ManagementTab = "admin" | "pm" | "submissions" | "delivery" | "transparency";

const personalTabs: { id: PersonalTab; label: string }[] = [
  { id: "feedback", label: "Give Feedback" },
  { id: "self", label: "Self Check" },
  { id: "results", label: "Feedback Received" },
];

const managementTabs: { id: ManagementTab; label: string; icon: typeof LayoutDashboard }[] = [
  { id: "admin", label: "Admin Dashboard", icon: LayoutDashboard },
  { id: "pm", label: "PM Overview", icon: BarChart3 },
  { id: "submissions", label: "Team Submissions", icon: UsersRound },
  { id: "delivery", label: "Delivery Overview", icon: ClipboardList },
  { id: "transparency", label: "Review Transparency", icon: Eye },
];

function WorkspaceTabs<T extends string>({ tabs, selected, path, label }: {
  tabs: { id: T; label: string; icon?: typeof LayoutDashboard }[];
  selected: T;
  path: string;
  label: string;
}) {
  return <div className={styles.switcher}>
    <div className={styles.tabs} role="group" aria-label={label}>
      {tabs.map(tab => <Link key={tab.id} to={`${path}?view=${tab.id}`}
        className={tab.id === selected ? styles.selected : styles.tab}
        aria-current={tab.id === selected ? "page" : undefined}>{tab.icon && <tab.icon size={17} aria-hidden="true" />}{tab.label}</Link>)}
    </div>
  </div>;
}

export function MyPerformancePage() {
  const [params] = useSearchParams();
  const requested = params.get("view");
  const selected = personalTabs.find(tab => tab.id === requested)?.id ?? "feedback";

  return <>
    <WorkspaceTabs tabs={personalTabs} selected={selected} path="/dashboard/myPerformance" label="My Performance views" />
    {selected === "feedback" ? <GiveFeedbackPage /> : selected === "self" ? <SelfCheckPage /> : <MyFeedbackPage />}
  </>;
}

export function PerformanceDashboardPage() {
  const { access, loading, error, refresh } = usePerformanceLensAccess();
  const [upcomingCycle, setUpcomingCycle] = useState<GiveFeedbackData["upcomingCycle"]>(null);
  const [params] = useSearchParams();
  const allowed = managementTabs.filter(tab =>
    tab.id === "admin" ? access?.admin === true
      : tab.id === "transparency" ? access?.transparency === true
      : tab.id === "delivery" ? !!access?.hodProjects?.length
        : !!access?.managedProjects?.length);
  const selected = allowed.find(tab => tab.id === params.get("view"))?.id ?? allowed[0]?.id;
  const showSchedule = !loading && !error && !!selected && selected !== "admin";

  useEffect(() => {
    if (!showSchedule) return;
    const controller = new AbortController();
    void getGiveFeedback(controller.signal).then(data => {
      if (!controller.signal.aborted) setUpcomingCycle(data.upcomingCycle ?? null);
    }).catch(() => { /* Management views remain available if schedule lookup fails. */ });
    return () => controller.abort();
  }, [showSchedule]);

  if (loading) return <PageShell><ScreenSkeleton label="Checking dashboard access" variant="dashboard" /></PageShell>;
  if (error) return <PageShell><div className={styles.message} role="alert">Could not verify dashboard access. <button type="button" onClick={() => void refresh()}>Retry</button></div></PageShell>;
  if (!selected) return <PageShell><div className={styles.message}>This dashboard is available to configured administrators, transparency viewers, and named Project Managers or Heads of Delivery.</div></PageShell>;

  return <>
    <WorkspaceTabs tabs={allowed} selected={selected} path="/dashboard/performanceDashboard" label="Performance management views" />
    {showSchedule && upcomingCycle && <div className={styles.managementSchedule}><UpcomingCycleBanner cycle={upcomingCycle} context="management" compact /></div>}
    {selected === "admin" ? <PerformanceLensAdminPage />
      : selected === "transparency" ? <TransparencyPage />
      : selected === "pm" ? <PmDashboardPage embedded />
        : selected === "submissions" ? <TeamSubmissionsPage embedded />
          : <HodOverviewPage />}
  </>;
}
